import { Textarea } from '@/components/ui/textarea';
import type { HqlCompileResult, HqlError } from '@rock/hql';
import type { FC } from 'react';

/**
 * A query box with its problems listed underneath, each quoting the text it's about. The parent
 * compiles the text (it usually needs the result too) and passes the result in.
 */
export const QueryInput: FC<{
    value: string;
    onChange: (value: string) => void;
    result: HqlCompileResult | undefined;
    placeholder?: string;
    autoFocus?: boolean;
}> = ({ value, onChange, result, placeholder, autoFocus }) => (
    <div className="space-y-2">
        <Textarea
            className="font-mono text-sm"
            rows={2}
            spellCheck={false}
            autoFocus={autoFocus}
            placeholder={placeholder}
            value={value}
            onChange={(event) => onChange(event.target.value)}
        />
        {result ? <QueryDiagnostics text={value} result={result} /> : null}
    </div>
);

/** Errors (red) and warnings (amber) for a query, each with the offending text highlighted. */
export const QueryDiagnostics: FC<{ text: string; result: HqlCompileResult }> = ({ text, result }) => {
    if (result.errors.length === 0 && result.warnings.length === 0) return null;
    return (
        <ul className="space-y-1 text-sm">
            {result.errors.map((error) => (
                <Diagnostic
                    key={`e${error.start}:${error.message}`}
                    text={text}
                    problem={error}
                    tone="text-destructive"
                />
            ))}
            {result.warnings.map((warning) => (
                <Diagnostic
                    key={`w${warning.start}:${warning.message}`}
                    text={text}
                    problem={warning}
                    tone="text-amber-700"
                />
            ))}
        </ul>
    );
};

const Diagnostic: FC<{ text: string; problem: HqlError; tone: string }> = ({ text, problem, tone }) => {
    const { before, marked, after } = excerpt(text, problem);
    return (
        <li className={tone}>
            {problem.message}
            {text.trim() ? (
                <div className="font-mono text-xs text-muted-foreground">
                    {before}
                    <mark className="rounded-sm bg-amber-200 px-0.5 text-foreground">{marked}</mark>
                    {after}
                </div>
            ) : null}
        </li>
    );
};

const CONTEXT = 24;

function excerpt(text: string, { start, end }: HqlError) {
    const from = Math.max(0, start - CONTEXT);
    const to = Math.min(text.length, end + CONTEXT);
    return {
        before: `${from > 0 ? '…' : ''}${text.slice(from, start)}`,
        marked: text.slice(start, end) || ' ',
        after: `${text.slice(end, to)}${to < text.length ? '…' : ''}`,
    };
}
